import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, PlugZap, X } from 'lucide-react';
import { isValidRemoteMcpUrl, REMOTE_URL_ERROR } from '../mcpConnectionService';
import { logMcp } from '../mcpLogger';
import type { AuthType, ConnectionFormValues, McpTestResult, Transport } from '../types';

const TRANSPORTS: Transport[] = ['stdio', 'sse', 'streamable-http'];
const AUTH_TYPES: AuthType[] = ['none', 'bearer', 'apiKey', 'header'];

type Props = {
  open: boolean;
  title: string;
  initial: ConnectionFormValues;
  onClose: () => void;
  onSave: (values: ConnectionFormValues) => Promise<void>;
  onSaveAndTest?: (values: ConnectionFormValues) => Promise<void>;
  onTestDraft?: (values: ConnectionFormValues) => Promise<McpTestResult>;
};

export default function ConnectionForm({
  open,
  title,
  initial,
  onClose,
  onSave,
  onSaveAndTest,
  onTestDraft,
}: Props) {
  const [values, setValues] = useState<ConnectionFormValues>(initial);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<McpTestResult | null>(null);

  useEffect(() => {
    if (open) {
      setValues(initial);
      setError(null);
      setTestResult(null);
      setSaving(false);
      setTesting(false);
    }
  }, [open, initial]);

  if (!open) return null;

  const set = <K extends keyof ConnectionFormValues>(key: K, value: ConnectionFormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  };

  const validate = (): boolean => {
    if (!values.name.trim()) {
      setError('Name is required');
      return false;
    }
    const code = values.code.trim().toUpperCase();
    if (values.source === 'catalog' && !code) {
      setError('Code is required for catalog connections');
      return false;
    }
    if (code && !/^[A-Z][A-Z0-9-]{2,63}$/.test(code)) {
      setError('Code must look like MCP-PLAYWRIGHT (A–Z, 0–9, hyphen)');
      return false;
    }
    if (values.transport === 'stdio' && !values.command.trim()) {
      setError('Command is required for stdio transport');
      return false;
    }
    if (values.transport !== 'stdio') {
      const url = values.url.trim();
      if (!url) {
        setError('URL is required for remote transports');
        return false;
      }
      if (!isValidRemoteMcpUrl(url)) {
        logMcp('warning', 'invalid_remote_mcp_url', { url });
        setError(REMOTE_URL_ERROR);
        return false;
      }
    }
    const isEdit = title.startsWith('Edit');
    if (values.authType !== 'none' && !values.authValue.trim() && !isEdit) {
      setError(`Auth value is required for ${values.authType}`);
      return false;
    }
    setError(null);
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(values);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndTest = async () => {
    if (!validate() || !onSaveAndTest) return;
    setSaving(true);
    setError(null);
    try {
      await onSaveAndTest(values);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save and test failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!validate() || !onTestDraft) return;
    if (values.authType !== 'none' && !values.authValue.trim()) {
      setError(`Auth value is required to test ${values.authType}`);
      return;
    }
    setTesting(true);
    setError(null);
    try {
      const result = await onTestDraft(values);
      setTestResult(result);
    } catch (err) {
      setTestResult(null);
      setError(err instanceof Error ? err.message : 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const isStdio = values.transport === 'stdio';
  const busy = saving || testing;
  const isCatalog = values.source === 'catalog';

  return createPortal(
    <div className="mcp-v2__overlay" role="presentation" onClick={busy ? undefined : onClose}>
      <div
        className="mcp-v2__modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mcp-v2-form-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mcp-v2__modal-header">
          <h2 id="mcp-v2-form-title">{title}</h2>
          <button
            type="button"
            className="mcp-v2__btn mcp-v2__btn--ghost"
            onClick={onClose}
            aria-label="Close"
            disabled={busy}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mcp-v2__modal-body">
          {error ? <div className="mcp-v2__alert mcp-v2__alert--error">{error}</div> : null}

          <div className="mcp-v2__row">
            <div className="mcp-v2__field">
              <label htmlFor="mcp-v2-name">Name *</label>
              <input
                id="mcp-v2-name"
                className="mcp-v2__input"
                value={values.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="My MCP server"
              />
            </div>
            <div className="mcp-v2__field">
              <label htmlFor="mcp-v2-code">
                Code {values.source === 'catalog' ? '*' : '(optional)'}
              </label>
              <input
                id="mcp-v2-code"
                className="mcp-v2__input"
                value={values.code}
                onChange={(e) => set('code', e.target.value.toUpperCase())}
                placeholder="MCP-PLAYWRIGHT"
                autoCapitalize="characters"
              />
            </div>
          </div>

          <div className="mcp-v2__field">
            <label htmlFor="mcp-v2-desc">Description</label>
            <textarea
              id="mcp-v2-desc"
              className="mcp-v2__textarea"
              rows={2}
              value={values.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="What this connection is used for"
            />
          </div>

          <div className="mcp-v2__row">
            <div className="mcp-v2__field">
              <label htmlFor="mcp-v2-transport">Transport *</label>
              <select
                id="mcp-v2-transport"
                className="mcp-v2__select"
                style={{ width: '100%' }}
                value={values.transport}
                onChange={(e) => set('transport', e.target.value as Transport)}
              >
                {TRANSPORTS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="mcp-v2__field">
              <label htmlFor="mcp-v2-auth">Auth</label>
              <select
                id="mcp-v2-auth"
                className="mcp-v2__select"
                style={{ width: '100%' }}
                value={values.authType}
                onChange={(e) => set('authType', e.target.value as AuthType)}
              >
                {AUTH_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isStdio ? (
            <>
              <div className="mcp-v2__field">
                <label htmlFor="mcp-v2-cmd">Command *</label>
                <input
                  id="mcp-v2-cmd"
                  className="mcp-v2__input"
                  value={values.command}
                  onChange={(e) => set('command', e.target.value)}
                  placeholder="npx"
                />
              </div>
              <div className="mcp-v2__field">
                <label htmlFor="mcp-v2-args">Args (one argument per line)</label>
                <textarea
                  id="mcp-v2-args"
                  className="mcp-v2__textarea"
                  rows={3}
                  value={values.argsText}
                  onChange={(e) => set('argsText', e.target.value)}
                  placeholder={'-y\n@modelcontextprotocol/server-filesystem\n/workspace'}
                />
              </div>
              <div className="mcp-v2__field">
                <label htmlFor="mcp-v2-env">Environment (JSON or KEY=value lines)</label>
                <textarea
                  id="mcp-v2-env"
                  className="mcp-v2__textarea"
                  rows={3}
                  value={values.envText}
                  onChange={(e) => set('envText', e.target.value)}
                  placeholder={'{\n  "API_KEY": ""\n}'}
                />
              </div>
            </>
          ) : (
            <>
              <div className="mcp-v2__field">
                <label htmlFor="mcp-v2-url">URL *</label>
                <input
                  id="mcp-v2-url"
                  className="mcp-v2__input"
                  value={values.url}
                  onChange={(e) => set('url', e.target.value)}
                  placeholder="http://127.0.0.1:8015/mcp or https://mcp.example.com/sse"
                />
              </div>
              <div className="mcp-v2__field">
                <label htmlFor="mcp-v2-headers">Headers (JSON or KEY=value lines)</label>
                <textarea
                  id="mcp-v2-headers"
                  className="mcp-v2__textarea"
                  rows={3}
                  value={values.headersText}
                  onChange={(e) => set('headersText', e.target.value)}
                  placeholder={'{\n  "X-Tenant": "demo"\n}'}
                />
              </div>
            </>
          )}

          {values.authType !== 'none' ? (
            <div className="mcp-v2__field">
              <label htmlFor="mcp-v2-auth-val">Auth value {title.startsWith('Edit') ? '(leave blank to keep)' : '*'}</label>
              <input
                id="mcp-v2-auth-val"
                className="mcp-v2__input"
                type="password"
                autoComplete="off"
                value={values.authValue}
                onChange={(e) => set('authValue', e.target.value)}
                placeholder={
                  values.authType === 'bearer'
                    ? 'Bearer token'
                    : values.authType === 'apiKey'
                      ? 'API key'
                      : 'Header value'
                }
              />
            </div>
          ) : null}

          <div className="mcp-v2__field">
            <label htmlFor="mcp-v2-tags">Tags (comma-separated)</label>
            <input
              id="mcp-v2-tags"
              className="mcp-v2__input"
              value={values.tagsText}
              onChange={(e) => set('tagsText', e.target.value)}
              placeholder="devtools, remote"
            />
          </div>

          <label className="mcp-v2__check">
            <input
              type="checkbox"
              checked={values.isActive}
              onChange={(e) => set('isActive', e.target.checked)}
            />
            Active
          </label>

          {testResult ? (
            <div className={`mcp-v2__alert ${testResult.success ? 'mcp-v2__alert--ok' : 'mcp-v2__alert--error'}`}>
              <strong>{testResult.success ? 'Connection succeeded' : 'Connection failed'}</strong>
              <span>
                {testResult.message} {testResult.latencyMs ? `(${testResult.latencyMs} ms)` : ''}
              </span>
              {testResult.success && testResult.tools.length > 0 ? (
                <span>
                  Tools: {testResult.tools.slice(0, 6).map((t) => t.name).join(', ')}
                  {testResult.tools.length > 6 ? ` +${testResult.tools.length - 6} more` : ''}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="mcp-v2__modal-footer">
          <button type="button" className="mcp-v2__btn mcp-v2__btn--outline" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          {onTestDraft ? (
            <button type="button" className="mcp-v2__btn mcp-v2__btn--outline" onClick={() => void handleTest()} disabled={busy}>
              {testing ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : <PlugZap className="w-4 h-4" aria-hidden />}
              {testing ? 'Testing…' : 'Test'}
            </button>
          ) : null}
          {onSaveAndTest ? (
            <button
              type="button"
              className={`mcp-v2__btn ${isCatalog ? 'mcp-v2__btn--primary' : 'mcp-v2__btn--outline'}`}
              onClick={() => void handleSaveAndTest()}
              disabled={busy}
            >
              {saving && isCatalog ? 'Saving…' : 'Save & test'}
            </button>
          ) : null}
          <button
            type="button"
            className={`mcp-v2__btn ${isCatalog ? 'mcp-v2__btn--outline' : 'mcp-v2__btn--primary'}`}
            onClick={() => void handleSave()}
            disabled={busy}
          >
            {saving && !isCatalog ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
